import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import * as Styles from './Highlight.styles';
import useLocale from '@/hooks/useLocale';
import {
  IconMail, IconLink, IconDashboard, Skeleton,
  Guide,
} from 'gatorcomponents';
import { AI_AGENTS_EMAIL_ENABLE_FRONT, ENABLE_STUDIO } from '@/config/GrowthBook/constants';
import HighlightContent from './HighlightContent/HighlightContent';
import { ALL_STUDIO_HIGHLIGHTS_IDS, PRODUCTS_HIGHLIGHT_IDS } from '@/pages/Home/HomeHandler.enum.jsx';
import withErrorBoundary from '@/utils/errorBoundary';
import IconAiPenMenu from '@/pages/Home/media/iconAiPenMenu';
import { FloatingToast } from '@/pages/common/FloatingToast';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

const Highlight = ({
  elementsHighlight, loading, size, focusOn, clearFocusOn, mountButtonIsLoading,
}) => {
  const defaultGuideCoordinates = {
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  };

  const [activeTab, setActiveTab] = useState(null);
  const [highLightImageLoaded, setHighLightImageLoaded] = useState(false);
  const [guideOn, setGuideOn] = useState(null);
  const [guideCoordinates, setGuideCoordinates] = useState(defaultGuideCoordinates);
  const [showToast, setShowToast] = useState(false);
  const [formattedMenuItems, setFormattedMenuItems] = useState([]);
  const [hgStudioBackground, setHgStudioBackground] = useState(false);

  const summaryState = useSelector(state => state.summary);
  const enabledAiAgentsMailChat = useFeatureIsOn(AI_AGENTS_EMAIL_ENABLE_FRONT);
  const enableStudio = useFeatureIsOn(ENABLE_STUDIO);

  const { home: homeLocale } = useLocale();

  const setGuideOff = useCallback(() => {
    const handleClick = () => guideOn === 'studio' && setGuideOn(null);

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [guideOn]);

  useEffect(() => {
    setGuideOff();
  }, [setGuideOff]);

  const handleActiveTab = (newActiveTab) => {
    setActiveTab(newActiveTab);
    if (ALL_STUDIO_HIGHLIGHTS_IDS.includes(newActiveTab)) {
      setHgStudioBackground(true);
    } else {
      setHgStudioBackground(false);
    }
  };

  const setFirstTab = useCallback(() => {
    const studioChip = elementsHighlight
      && elementsHighlight.length
      && elementsHighlight.find(element => ALL_STUDIO_HIGHLIGHTS_IDS.includes(element.id));

    if (focusOn === 'studio' && studioChip) {
      handleActiveTab(studioChip.id);
      setGuideOn('studio');
      clearFocusOn();
      return;
    }
    if (elementsHighlight && elementsHighlight.length > 0 && activeTab === null) {
      setActiveTab(elementsHighlight[0].id);
    }
  }, [elementsHighlight, activeTab, clearFocusOn, focusOn]);

  const formatMenuItems = useCallback(() => {
    const menuItemsFormatted = [];
    elementsHighlight.forEach((element) => {
      if (element.id === PRODUCTS_HIGHLIGHT_IDS.SITE_CREATOR) {
        menuItemsFormatted.push({
          icon: <IconDashboard color="primaryExtraDark" />,
          title: homeLocale.infosMenuHighlight.menuHighlightTitle1,
          id: element.id,
        });
      }
      if (ALL_STUDIO_HIGHLIGHTS_IDS.includes(element.id) && enableStudio) {
        menuItemsFormatted.push({
          icon: <IconAiPenMenu />,
          title: homeLocale.infosMenuHighlight.menuHighlightTitle4,
          id: element.id,
        });
      }
      if (element.id === PRODUCTS_HIGHLIGHT_IDS.EMAIL_TITAN && enabledAiAgentsMailChat) {
        menuItemsFormatted.push({
          icon: <IconMail color="primaryExtraDark" />,
          title: homeLocale.infosMenuHighlight.menuHighlightTitle2,
          id: element.id,
        });
      }
      if (element.id === PRODUCTS_HIGHLIGHT_IDS.LINK_BIO) {
        menuItemsFormatted.push({
          icon: <IconLink color="primaryExtraDark" />,
          title: homeLocale.infosMenuHighlight.menuHighlightTitle3,
          id: element.id,
        });
      }
    });
    setFormattedMenuItems(menuItemsFormatted);
  }, [elementsHighlight, homeLocale]);

  const verifyVinculedGoogle = useCallback(() => {
    setShowToast(summaryState.showOAuthUserLink);
  }, [summaryState]);

  useEffect(() => {
    verifyVinculedGoogle();
  }, [verifyVinculedGoogle]);

  useEffect(() => {
    setFirstTab();
  }, [setFirstTab]);

  useEffect(() => {
    if (elementsHighlight && elementsHighlight.length > 0) {
      formatMenuItems();
    }
  }, [formatMenuItems, elementsHighlight]);

  const guideProps = useCallback(() => {
    const popoverContent = (
      <Styles.GuidePopoverContent>
        <Styles.GuidePopoverTitle>{homeLocale.infosMenuHighlight.studioGuide.title}</Styles.GuidePopoverTitle>
        <Styles.GuidePopoverDescription>{homeLocale.infosMenuHighlight.studioGuide.description}</Styles.GuidePopoverDescription>
      </Styles.GuidePopoverContent>
    );

    const maskPadding = 16;

    const steps = [
      {
        popover: {
          content: popoverContent,
          $top: guideCoordinates.top + guideCoordinates.height + (maskPadding * 2),
          $left: guideCoordinates.left - (guideCoordinates.width + maskPadding),
          pointer: 'topRight',
          $maxWidth: 285,
        },
        mask: {
          $width: guideCoordinates.width + (maskPadding * 2),
          $height: guideCoordinates.height + (maskPadding * 2),
          $top: guideCoordinates.top - maskPadding,
          $left: guideCoordinates.left - maskPadding,
        },
      },
    ];

    return {
      steps,
    };
  }, [guideCoordinates, homeLocale]);

  const highLightContentProps = useCallback((element) => {
    const props = {
      key: element.name,
      element,
      activeTab,
      setGuideCoordinates,
      highLightImageLoaded,
      setHighLightImageLoaded,
      buttonIsLoading: mountButtonIsLoading(element.id),
      guideOn,
    };

    return props;
  }, [highLightImageLoaded, activeTab, mountButtonIsLoading, guideOn]);

  if (!activeTab || loading) {
    return (
      <Styles.HighlightWrapper skeleton width={size} data-testid="menu-highlight">
        <Styles.MenuHighlight data-testid="menu-highlight-loading">
          <Skeleton height="32px" width="164px" variant="button" />
          <Skeleton height="32px" width="164px" variant="button" />
          <Skeleton height="32px" width="164px" variant="button" />
        </Styles.MenuHighlight>
        <Styles.SkeletonWrapper>
          <Skeleton height="200px" width="350px" variant="rectangle" />
          <Styles.SkeletonWrapper column alignStart>
            <Skeleton height="64px" width="450px" variant="paragraph" />
            <Skeleton height="32px" width="164px" variant="button" />
          </Styles.SkeletonWrapper>
        </Styles.SkeletonWrapper>
      </Styles.HighlightWrapper>
    );
  }

  return (
    <Styles.BackgroundWrapper backgroundStudio={hgStudioBackground}>
      {guideOn && highLightImageLoaded && (
        <Styles.GuideContainer>
          <Guide {...guideProps()} />
        </Styles.GuideContainer>
      )}

      <Styles.HighlightWrapper width={size}>
        <Styles.MenuHighlight data-testid="menu-highlight">
          {formattedMenuItems && formattedMenuItems.map(menuItem => (
            <Styles.MenuHighlightButton
              onClick={() => handleActiveTab(menuItem.id)}
              active={activeTab === menuItem.id}
              key={menuItem.id}
            >
              <Styles.MenuHighlightIcon>
                {menuItem.icon}
              </Styles.MenuHighlightIcon>

              <Styles.MenuHighlightTitle dangerouslySetInnerHTML={{ __html: menuItem.title }} />
            </Styles.MenuHighlightButton>
          ))}
        </Styles.MenuHighlight>

        {showToast && (
          <FloatingToast
            title={homeLocale.VinculedGoogle.title}
            message={homeLocale.VinculedGoogle.message}
            context="success"
            duration={3000}
            onClose={() => setShowToast(false)}
          />
        )}

        {elementsHighlight.map(element => (
          element.id === activeTab && (
            <HighlightContent {...highLightContentProps(element)} key={element.id} />
          )
        ))}
      </Styles.HighlightWrapper>
    </Styles.BackgroundWrapper>
  );
};

export default withErrorBoundary(Highlight, 'Highlight');
